import { BrowsingContextError } from '../../browsingContextError';
import { createIterable, isEmptyIterable } from '../iterable';
import { isOnlyCollapsibleWhitespace } from '../whitespace';
import { isBreakElement } from './paragraph';
import {
  createPointAtEndOfNode,
  createPointAtStartOfNode,
  createPointWithEndOfRange,
  createPointWithStartOfRange,
} from './point';

export function isNode(maybeNode: any): maybeNode is Node {
  return typeof maybeNode === 'object' && 'childNodes' in maybeNode;
}

export function isElement(node: Node | null): node is Element {
  return !!node && node.nodeType === Node.ELEMENT_NODE;
}

export function isElementWithTagName<K extends keyof HTMLElementTagNameMap>(
  node: Node | null,
  tagName: K,
): node is HTMLElementTagNameMap[K] {
  return isElement(node) && node.tagName.toLowerCase() === tagName.toLowerCase();
}

export function isHTMLOrSVGElement(node: Node | null): node is HTMLElement | SVGElement {
  return isElement(node) && 'style' in node;
}

export function isText(node: Node | null): node is Text {
  return !!node && node.nodeType === Node.TEXT_NODE;
}

export function isComment(node: Node | null): node is Text {
  return !!node && node.nodeType === Node.COMMENT_NODE;
}

export function isFragment(node: Node | null): node is DocumentFragment {
  return !!node && node.nodeType === Node.DOCUMENT_FRAGMENT_NODE;
}

export function isChildNode(node: Node | null): node is Node & ChildNode {
  return !!node && (isElement(node) || isText(node) || isComment(node));
}

export function getStartNode(node: Node): (Node & ChildNode) | null {
  return !isChildNode(node) ? node.firstChild : node;
}

export function getEndNode(node: Node): (Node & ChildNode) | null {
  return !isChildNode(node) ? node.lastChild : node;
}

export function getChildNode(node: Node, index: number): (Node & ChildNode) | null {
  const maybeChildNode = node.childNodes[index] as ChildNode | undefined;
  return maybeChildNode || null;
}

export function isWhitespaceText(node: Node): boolean {
  // Don't include things like non-breaking spaces, because they are significant.
  return isText(node) && isOnlyCollapsibleWhitespace(node.data);
}

export function trimWhitespaceTextFromNode(node: Node) {
  trimWhitespaceTextFromStartOfNode(node);
  trimWhitespaceTextFromEndOfNode(node);
}

export function trimWhitespaceTextFromStartOfNode(node: Node) {
  while (node.firstChild && isWhitespaceText(node.firstChild)) {
    node.removeChild(node.firstChild);
  }
}

export function trimWhitespaceTextFromEndOfNode(node: Node) {
  while (node.lastChild && isWhitespaceText(node.lastChild)) {
    node.removeChild(node.lastChild);
  }
}

export function hasAncestor(node: Node, predicate: (ancestor: Node) => boolean, container: Node): boolean {
  return !!findAncestor(node, predicate, container);
}

export function findAncestor<T extends Node>(
  node: Node,
  predicate: (ancestor: Node) => ancestor is T,
  container: Node,
): T | null;
export function findAncestor(
  node: Node,
  predicate: (ancestor: Node) => boolean,
  container: Node,
): Node | null;
export function findAncestor(node: Node, predicate: (ancestor: Node) => any, container: Node): Node | null {
  let parentNode: Node | null = node;

  while (parentNode) {
    if (predicate(parentNode)) {
      return parentNode;
    }

    parentNode = parentNode !== container ? parentNode.parentNode : null;
  }

  return null;
}

export function isContentEditableInEditor(node: Node, container: Node): boolean {
  function isContentEditableOrContainer(ancestor: Node | null): ancestor is Element {
    return ancestor === container || (isElement(ancestor) && ancestor.hasAttribute('contenteditable'));
  }

  const ancestor = findAncestor(node, isContentEditableOrContainer, container);
  if (ancestor === container) {
    // If we've reached the container
    return true;
  }
  if (ancestor === null) {
    // This shouldn't happen unless the node is unmounted.
    return false;
  }
  return ancestor.getAttribute('contenteditable') !== 'false';
}

export function findAncestorSiblings(
  range: Range,
  predicate: (ancestor: Node) => boolean,
  container: Node,
): { startAncestor: Node; endAncestor: Node } | null {
  let startAncestor = findAncestor(range.startContainer, predicate, container);
  let endAncestor = findAncestor(range.endContainer, predicate, container);
  if (!startAncestor || !endAncestor) {
    return null;
  }

  if (startAncestor === endAncestor) {
    return { startAncestor, endAncestor };
  }

  const commonAncestorContainer = findCommonAncestor(startAncestor, endAncestor, container);
  if (!commonAncestorContainer) {
    return null;
  }

  const childNodes = Array.from(commonAncestorContainer.childNodes);
  startAncestor = childNodes.find(childNode => childNode.contains(startAncestor)) || startAncestor;
  endAncestor = childNodes.find(childNode => childNode.contains(endAncestor)) || endAncestor;
  return { startAncestor, endAncestor };
}

export function findNearestElement(node: Node, container: Element): Element;
export function findNearestElement(node: Node, container: Node): Element | null;
export function findNearestElement(node: Node, container: Node): Element | null {
  return findAncestor(node, isElement, container);
}

export function findCommonAncestor(a: Node, b: Node, container: Node): Node | null {
  return findAncestor(a, ancestor => ancestor.contains(b), container);
}

export function findNextSibling(node: Node, predicate: (sibling: Node) => boolean = () => true): Node | null {
  let sibling = node.nextSibling;

  while (sibling) {
    if (predicate(sibling)) {
      return sibling;
    }

    sibling = sibling.nextSibling;
  }

  return null;
}

export function findPreviousSibling(
  node: Node,
  predicate: (sibling: Node) => boolean = () => true,
): Node | null {
  let sibling = node.previousSibling;

  while (sibling) {
    if (predicate(sibling)) {
      return sibling;
    }

    sibling = sibling.previousSibling;
  }

  return null;
}

export function findNextNonWhitespaceSibling(node: Node): Node | null {
  return findNextSibling(node, sibling => !isWhitespaceText(sibling));
}

export function findPreviousNonWhitespaceSibling(node: Node): Node | null {
  return findPreviousSibling(node, sibling => !isWhitespaceText(sibling));
}

/**
 * Returns the next node in a depth-first preorder traversal.
 *
 * @param node
 *   The current position in the tree.
 * @param container
 *   The root of the tree.
 *
 * @returns
 *   The first child, next sibling, next aunt/uncle, etc. or null if we finished traversing the tree.
 */
export function findNextChildOrSibling(node: Node, container: Node): Node | null {
  if (node.firstChild) {
    return node.firstChild;
  }

  return findNextSiblingOrAuncle(node, container);
}

/**
 * Returns the next node in a depth-first preorder traversal, after skipping the subtree rooted at `node`.
 *
 * @param node
 *   The current position in the tree.
 * @param container
 *   The root of the tree.
 *
 * @returns
 *   The next sibling, next aunt/uncle, etc. or null if we finished traversing the tree.
 */
export function findNextSiblingOrAuncle(node: Node, container: Node): Node | null {
  const ancestorWithNextSibling: Node | null = findAncestor(
    node,
    ancestor => !!ancestor.nextSibling,
    container,
  );

  return ancestorWithNextSibling && ancestorWithNextSibling !== container
    ? ancestorWithNextSibling.nextSibling
    : null;
}

/** @see findNextChildOrSibling */
export function findPreviousChildOrSibling(node: Node, container: Node): Node | null {
  if (node.lastChild) {
    return node.lastChild;
  }

  return findPreviousSiblingOrAuncle(node, container);
}

/** @see findNextSiblingOrAuncle */
export function findPreviousSiblingOrAuncle(node: Node, container: Node): Node | null {
  const ancestorWithPreviousSibling: Node | null = findAncestor(
    node,
    ancestor => !!ancestor.previousSibling,
    container,
  );

  return ancestorWithPreviousSibling && ancestorWithPreviousSibling !== container
    ? ancestorWithPreviousSibling.previousSibling
    : null;
}

export function getTextNodes(root: Node): Iterable<Text> {
  const document = root.ownerDocument;
  if (!document) {
    return [];
  }

  return createIterable(function* createIterator() {
    const iterator = document.createNodeIterator(root, NodeFilter.SHOW_TEXT, {
      acceptNode: candidateNode =>
        isText(candidateNode) ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_REJECT,
    });

    let node: Text | null;
    while ((node = iterator.nextNode() as Text | null)) {
      yield node;
    }
  });
}

export function wrapNode<T extends Node, U extends Element>(
  node: T,
  createElement: (document: Document) => U,
): T | U {
  const { ownerDocument: document, parentNode } = node;
  if (!document || !parentNode) {
    return node;
  }

  const element = createElement(document);
  parentNode.insertBefore(element, node);
  element.appendChild(node);
  return element;
}

export function removeChildren(node: Node, shouldRemove: (node: Node) => boolean) {
  Array.from(node.childNodes).forEach(childNode => shouldRemove(childNode) && node.removeChild(childNode));
}

export function removeAllChildren(node: Node) {
  removeChildren(node, () => true);
}

export function replaceAllChildren(node: Node, newChild: Node) {
  removeAllChildren(node);
  node.appendChild(newChild);
}

export function findNodeInRange<T extends Node>(
  container: Node,
  range: Range,
  predicate: (node: Node) => T | null,
): T | null;
export function findNodeInRange<T extends Node>(
  container: Node,
  range: Range,
  predicate: (node: Node) => node is T,
): T | null;
export function findNodeInRange(
  container: Node,
  range: Range,
  predicate: (node: Node) => boolean,
): Node | null;
export function findNodeInRange(
  container: Node,
  range: Range,
  predicate: (node: Node) => boolean | Node | null,
): Node | null {
  const startPoint = createPointWithStartOfRange(range).moveToStart();
  const endPoint = createPointWithEndOfRange(range).moveToEnd();

  let node = startPoint.findNextNode(container);
  while (node) {
    if (endPoint.intersectsNode(node) && !endPoint.isAtEndOfNode(node)) {
      node = node.firstChild;
      continue;
    }

    if (!endPoint.isAfterNode(node)) {
      node = null;
      break;
    }

    const result = predicate(node);
    if (result) {
      return typeof result === 'boolean' ? node : result;
    }

    node = findNextChildOrSibling(node, container);
  }

  return null;
}

export function isTextLikeNode(node: Node | null): node is Text | HTMLBRElement {
  return isText(node) || isBreakElement(node);
}

export function findFirstTextLikeNode(container: Node, startNode?: Node): Text | HTMLBRElement | null {
  if (startNode && isTextLikeNode(startNode)) {
    return startNode;
  }

  const document = container.ownerDocument;
  if (!document) {
    return null;
  }

  const walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT | NodeFilter.SHOW_ELEMENT, {
    acceptNode: candidateNode =>
      isTextLikeNode(candidateNode) ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_SKIP,
  });
  walker.currentNode = startNode || container;

  return walker.nextNode() as Text | HTMLBRElement | null;
}

export function findLastTextLikeNode(
  container: Node,
  startNode?: Node,
  maxIterations = Infinity,
): Text | HTMLBRElement | null {
  let numIterations = 0;
  let node: Node | null = startNode || findPreviousChildOrSibling(container, container);

  while (numIterations < maxIterations && node && !isTextLikeNode(node)) {
    numIterations += 1;
    node = findPreviousChildOrSibling(node, container);
  }

  return isTextLikeNode(node) ? node : null;
}

export function hasNodesIntersectingRange(
  container: Node,
  range: Range,
  condition: (element: Node) => boolean,
): boolean {
  return !isEmptyIterable(getNodesIntersectingRange(container, range, condition));
}

/**
 * This function can have unexpected behaviors because it won't return absolutely ALL nodes in range respecting condition:
 * - If a node is strictly included in the range and matches the condition we'll yield it.
 *   Next considered node will be its sibling or the next sibling of its first ancestor having a next sibling.
 *   Its child nodes will never be considered, so even if they match condition, they won't be returned.
 * - If a node is strictly included in range and does not match condition, we skip it.
 *   Next considered node will be its first child, or its next sibling, or the next sibling of its first ancestor having a next sibling.
 * - If a node contains range's end (ie: is not strictly included in range: it starts in it, but ends out of it):
 *   - If it respect condition and strictMode is set to false, we yield it, we skip it otherwise.
 *   - Further candidate research domain is now restrained to this node.
 *   - Next considered node will be its first child.
 *
 * @param container: container used to limit the search for next siblings.
 * @param range: range in which we'll look for nodes.
 * @param condition: condition a node need to match to be returned (it is a necessary condition but not a sufficient condition).
 * @param strictMode: determine if node intersecting with range's end is returned if it matchs condition
 */
export function getNodesIntersectingRange<T extends Node>(
  container: Node,
  range: Range,
  condition: (node: Node) => node is T,
  strictMode?: boolean,
): Iterable<T>;
export function getNodesIntersectingRange(
  container: Node,
  range: Range,
  condition: (node: Node) => boolean,
  strictMode?: boolean,
): Iterable<Node>;
export function* getNodesIntersectingRange(
  container: Node,
  range: Range,
  condition: (node: Node) => boolean,
  strictMode: boolean = true,
): Iterable<Node> {
  // Never hoist up to the container even it matches the condition, because we want to stay inside of it.
  const notContainerCondition = (node: Node) => node !== container && condition(node);

  // Hoist up to matching nodes that intersect the start/end points, if any.
  const { startContainer, endContainer, endOffset, collapsed } = range;
  const startNode = findAncestor(startContainer, notContainerCondition, container);

  // Don't count the end point as an intersection if the range is not collapsed and it's at the start of its container.
  const isEndContainerIntersecting = collapsed || endOffset > 0;
  const endNode = isEndContainerIntersecting
    ? findAncestor(endContainer, notContainerCondition, container)
    : null;

  let startPoint;
  if (!startNode) {
    startPoint = createPointWithStartOfRange(range);
  } else {
    startPoint = createPointAtStartOfNode(startNode);
  }

  let endPoint;
  if (!endNode) {
    endPoint = createPointWithEndOfRange(range);
  } else {
    endPoint = createPointAtEndOfNode(endNode);
  }

  let traversalContainer: Node = container;
  let node: Node | null = startPoint.findNextNode(traversalContainer);
  let prevNode: Node | null = null;
  while (node) {
    // Infinite loop protection.
    if (node === prevNode) {
      break;
    }
    prevNode = node;

    // Narrow our search scope if the current node contains the end point.
    if (endPoint.intersectsNode(node)) {
      traversalContainer = node;

      // If the current node non-inclusively contains the end point, not all children are in range, and so we
      // traverse down (preorder depth-first).
      // This node won't be yield in strictMode, even if it matches condition.
      if (!endPoint.isAtEndOfNode(node)) {
        if (!strictMode && condition(node)) {
          yield node;
        }
        node = node.firstChild;
        continue;
      }
    }

    // Stop when we've reached the end.
    if (!endPoint.isAfterNode(node)) {
      node = null;
      break;
    }

    if (condition(node)) {
      yield node;
      node = findNextSiblingOrAuncle(node, container);
    } else {
      node = findNextChildOrSibling(node, container);
    }
  }
}

export function getNodeContents(node: Node): DocumentFragment {
  const document = node.ownerDocument;
  if (!document) {
    throw new BrowsingContextError();
  }

  const replacement = document.createDocumentFragment();

  // Transplant the children.
  let childNode: Node | null = node.firstChild;
  while (childNode) {
    const nextChildNode = childNode.nextSibling;
    replacement.appendChild(childNode);
    childNode = nextChildNode;
  }

  return replacement;
}

export function unwrapNode(node: Node) {
  const { parentNode } = node;
  if (!parentNode) {
    return;
  }

  const childNodes = getNodeContents(node);
  parentNode.insertBefore(childNodes, node);
  parentNode.removeChild(node);
}
